import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Sparkles, X, Loader2, CheckCircle, AlertCircle, Play, Download, Eye, Upload as UploadIcon, Clock } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const ImageEnhancementNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const getInputForNode = useWorkflowStore((state) => state.getInputForNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  // Check if this node has an input connected
  const hasInput = edges.some((edge) => edge.target === id);
  const inputData = getInputForNode(id);

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.url) {
        updateNodeData(id, { galleryUrl: result.url, galleryFileName: result.fileName });
      } else if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          updateNodeData(id, { galleryUrl: e.target.result, galleryFileName: result.file.name });
        };
        reader.readAsDataURL(result.file);
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker, updateNodeData]);

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProcess = useCallback(async () => {
    const input = getInputForNode(id);
    const effectiveInput = input || (data.galleryUrl ? { outputUrl: data.galleryUrl, fileName: data.galleryFileName } : null);
    if (!effectiveInput) return false;

    resetExec();

    let imageFile = effectiveInput.file;
    if (!imageFile) {
      const fileUrl = effectiveInput.outputUrl || effectiveInput.preview;
      if (fileUrl) {
        // If already an HTTP URL (R2), pass directly as param to avoid re-upload
        if (fileUrl.startsWith('http')) {
          const result = await processNode({
            resultType: 'enhance_photo',
            params: { image_url: fileUrl },
          });
          if (result?.outputUrl) {
            updateNodeData(id, { hasOutput: true, outputUrl: result.outputUrl, assetId: result.assetId ?? null, processedAt: new Date().toISOString() });
            addAsset({ id: `asset_${Date.now()}`, type: 'image', tool: 'enhance', parent: effectiveInput.fileName, filename: `enhanced_${effectiveInput.fileName}`, url: result.outputUrl, createdAt: new Date().toISOString() });
            return true;
          }
          return false;
        }
        const blobFile = await blobToFile(fileUrl, effectiveInput.fileName || 'input.jpg', 'image/jpeg');
        if (!blobFile) {
          console.error('Could not load image from URL');
          return false;
        }
        imageFile = blobFile;
      }
    }
    if (!imageFile) return false;

    const result = await processNode({
      resultType: 'enhance_photo',
      files: [{ key: 'image_url', file: imageFile }],
    });

    if (result?.outputUrl) {
      updateNodeData(id, { hasOutput: true, outputUrl: result.outputUrl, assetId: result.assetId ?? null, processedAt: new Date().toISOString() });
      addAsset({ id: `asset_${Date.now()}`, type: 'image', tool: 'enhance', parent: effectiveInput.fileName, filename: `enhanced_${effectiveInput.fileName}`, url: result.outputUrl, createdAt: new Date().toISOString() });
      return true;
    }
    return false;
  }, [id, data.galleryUrl, data.galleryFileName, getInputForNode, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', !!((hasInput && inputData?.hasOutput) || data.galleryUrl), { connected: hasInput })]);

  return (
    <div className={`node-container enhancement-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="enhance-reconfigure-notice"
        />
      )}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
      />
      
      {/* Header */}
      <div className="node-header enhancement-header">
        <div className="node-header-left">
          <Sparkles size={16} />
          <span>Mejorar Imagen</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content">
        {/* Input Status */}
        <div className={`node-input-status ${(hasInput && inputData?.hasOutput) || data.galleryUrl ? 'connected' : 'disconnected'}`}>
          {(hasInput && inputData?.hasOutput) || data.galleryUrl ? (
            <>
              <CheckCircle size={12} />
              <span>{data.galleryUrl ? 'Desde galería' : 'Entrada conectada'}</span>
            </>
          ) : (
            <>
              <AlertCircle size={12} />
              <span>Sin entrada</span>
              <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-enhance">
                <UploadIcon size={12} /> Seleccionar
              </button>
            </>
          )}
        </div>

        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivo...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Mejorando imagen...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <img 
              src={data.outputUrl} 
              alt="Enhanced" 
              className="node-result-image clickable" 
              onClick={() => openImagePreview(data.outputUrl, 'Imagen Mejorada')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="enhance-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="enhance-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openImagePreview(data.outputUrl, 'Imagen Mejorada')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `enhanced_${Date.now()}.png`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="imageenhancement" testIdPrefix="imageenhancement-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Completado</span>
            </div>
          </div>
        ) : (
          <div className="node-action-area">
            <p className="node-description">
              Restaura y mejora la calidad de la imagen usando IA
            </p>
            <button
              className="node-process-btn"
              onClick={handleProcess}
              disabled={!((hasInput && inputData?.hasOutput) || data.galleryUrl)}
              title={requirementsTitle(runMissing)}
            >
              <Play size={14} />
              Procesar
            </button>
            <NodeRequirements missing={runMissing} />
          </div>
        )}

        {/* Error Display */}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

ImageEnhancementNode.displayName = 'ImageEnhancementNode';

export default ImageEnhancementNode;
